import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import {
  datetimeLocalToIso,
  toDatetimeLocalInput,
} from "@/core/utils/func/format-date";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { SimulateKycCaseApprovedRequest } from "@/features/notification/api/dto";
import { useSimulateKycCaseApproved } from "@/features/notification/api/use-simulate-kyc-case-approved";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";
import {
  createSimulateKycApprovedSchema,
  type SimulateKycApprovedFormValues,
} from "@/features/notification/schemas/simulate-event.schema";
import { PREFERRED_LANGUAGES } from "@/features/shared/api/enums";
import {
  useKeycloakUserSelectOptions,
  usePartySelectOptions,
} from "@/features/shared/api";

const EMPTY_VALUES: SimulateKycApprovedFormValues = {
  eventId: "",
  partyId: "",
  keycloakUserId: "",
  recipientAddress: "",
  preferredLanguage: "FR",
  level: "TIER1",
  approvedAt: "",
};

const freshValues = (): SimulateKycApprovedFormValues => ({
  ...EMPTY_VALUES,
  eventId: crypto.randomUUID(),
  approvedAt: toDatetimeLocalInput(),
});

const toPayload = (
  values: SimulateKycApprovedFormValues
): SimulateKycCaseApprovedRequest => ({
  eventId: values.eventId.trim(),
  partyId: values.partyId.trim(),
  keycloakUserId: values.keycloakUserId.trim(),
  recipientAddress: values.recipientAddress.trim(),
  preferredLanguage: values.preferredLanguage,
  level: values.level.trim(),
  approvedAt: datetimeLocalToIso(values.approvedAt.trim()),
});

/**
 * Dialog de simulation `simulate-kyc-case-approved` — envoie une notification
 * RÉELLE d'approbation KYC. Identifiants : selects searchables
 * (`POST /Parties/all`, parties+agents pour le `sub` Keycloak). `eventId`
 * reste un UUID client (déduplication — pas de liste d’événements).
 */
export function SimulateKycApprovedDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { t: tNotif } = useNotificationsTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- schéma zod traduit reconstruit sur changement de langue (règle projet).
  const schema = useMemo(() => createSimulateKycApprovedSchema(), [i18n.language]);

  const simulate = useSimulateKycCaseApproved();
  const pending = simulate.isPending;
  const [preview, setPreview] = useState<SimulateKycCaseApprovedRequest | null>(
    null
  );
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);
  const { options: keycloakOptions, isLoading: keycloakLoading } =
    useKeycloakUserSelectOptions(open);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  useEffect(() => {
    if (!open) {
      setPreview(null);
      form.reset(EMPTY_VALUES);
      return;
    }
    form.reset(freshValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset ciblé à l'ouverture.
  }, [open]);

  const fieldError =
    (name: keyof SimulateKycApprovedFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const handleConfirm = () => {
    if (!preview) return;
    simulate.mutate(preview, {
      onSuccess: () => {
        notifSilence(tNotif("notifications.events.feedback.sent"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(apiErrorHandler(error)),
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{tNotif("notifications.events.kycApproved.title")}</DialogTitle>
            <DialogDescription>
              {tNotif("notifications.events.warning")}
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="eventId" validators={{ onChange: fieldError("eventId") }}>
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="ka-eventId">
                    {tNotif("notifications.events.common.eventId")}
                  </Label>
                  <Input
                    id="ka-eventId"
                    value={field.state.value}
                    placeholder={tNotif("notifications.events.common.eventIdPlaceholder")}
                    className="notranslate font-mono"
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="partyId" validators={{ onChange: fieldError("partyId") }}>
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="ka-partyId">
                    {tNotif("notifications.events.common.partyId")}
                  </Label>
                  <EntitySearchableSelect
                    id="ka-partyId"
                    options={partyOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tNotif("notifications.events.common.partyIdPlaceholder")}
                    isLoading={partiesLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="keycloakUserId"
              validators={{ onChange: fieldError("keycloakUserId") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="ka-kcId">
                    {tNotif("notifications.events.common.keycloakUserId")}
                  </Label>
                  <EntitySearchableSelect
                    id="ka-kcId"
                    options={keycloakOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tNotif(
                      "notifications.events.common.keycloakUserIdPlaceholder"
                    )}
                    isLoading={keycloakLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="recipientAddress"
              validators={{ onChange: fieldError("recipientAddress") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="ka-recipient">
                    {tNotif("notifications.events.common.recipientAddress")}
                  </Label>
                  <Input
                    id="ka-recipient"
                    className="notranslate"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.common.recipientAddressPlaceholder"
                    )}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="preferredLanguage"
              validators={{ onChange: fieldError("preferredLanguage") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{tNotif("notifications.events.common.preferredLanguage")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(
                        v as SimulateKycApprovedFormValues["preferredLanguage"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PREFERRED_LANGUAGES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field name="level" validators={{ onChange: fieldError("level") }}>
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="ka-level">
                    {tNotif("notifications.events.kycApproved.level")}
                  </Label>
                  <Input
                    id="ka-level"
                    className="notranslate"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.kycApproved.levelPlaceholder"
                    )}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="approvedAt"
              validators={{ onChange: fieldError("approvedAt") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="ka-approvedAt">
                    {tNotif("notifications.events.kycApproved.approvedAt")}
                  </Label>
                  <Input
                    id="ka-approvedAt"
                    type="datetime-local"
                    value={field.state.value}
                    className="notranslate"
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {tNotif("notifications.events.confirm.action")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview}
        title={tNotif("notifications.events.confirm.title")}
        description={tNotif("notifications.events.confirm.description")}
        t={tNotif}
        confirmLabel={tNotif("notifications.events.confirm.action")}
        pending={pending}
        onConfirm={handleConfirm}
      />
    </>
  );
}
